// utilities/authInterceptor.js - adds the access token to every API call (default axios instance).
// Installed once in index.js; every file in src/api uses the default axios import, so no API
// file needs changes.
//
// While the server runs Auth.Mode Off / LogOnly a missing or failed token never blocks or logs
// anyone out. Only when an API call itself returns 401 (Auth.Mode Enforce) do we refresh and
// retry once, and end the session if the refresh is refused.
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import sessionManager from './SessionManager';
import { getAccessToken, refreshAccessToken, isTokenMode, clearTokens } from './authToken';

const LOGIN_PATHS = ['/Security/GetValidEmployee', '/Security/GetValidUser'];

const isApiCall = (url = '') => url.startsWith(API_BASE_URL);
const isLoginCall = (url = '') => LOGIN_PATHS.some((path) => url.includes(path));
const isAuthCall = (url = '') => url.includes('/Auth/');

let installed = false;

export const installAuthInterceptor = () => {
    if (installed) return;
    installed = true;

    axios.interceptors.request.use(async (config) => {
        const url = config.url || '';
        if (!isApiCall(url) || isAuthCall(url)) return config;

        if (isLoginCall(url)) {
            // Lets the browser store the refresh cookie the login response sets
            config.withCredentials = true;
            return config;
        }

        const token = await getAccessToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { config, response } = error;
            const url = config?.url || '';

            if (response?.status !== 401 || !config || config._authRetried
                || !isApiCall(url) || isAuthCall(url) || isLoginCall(url) || !isTokenMode()) {
                return Promise.reject(error);
            }

            config._authRetried = true;
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                const token = await getAccessToken();
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${token}`;
                return axios(config);
            }

            // Session ended on the server (expired, logged out elsewhere, password changed)
            clearTokens();
            sessionManager.handleSessionExpiry();
            return Promise.reject(error);
        }
    );
};

export default installAuthInterceptor;
